/**
 * Master-design react-select menu styling (BM-9882), mirroring
 * `NewSelectMultiSelect`'s `buildStyles` (single-select) so every status dropdown
 * (sample status, licensing, customer) opens with the exact menu/option look of
 * the PIM master-design select — the finalised UI.
 *
 * Kept as a plain style map so it can be spread into each control's own `styles`
 * object without pulling in the full NewSelectMultiSelect wrapper.
 */
const COLOR_TEXT = '#3c3c3c';
const COLOR_OPTION_HOVER = '#ceeafc';
const COLOR_OPTION_SELECTED = '#eaf6fe';
const FONT_MEDIUM = "'Aktiv Grotesk', sans-serif";

type StyleBase = Record<string, unknown>;
type OptionState = { isFocused?: boolean; isSelected?: boolean; isDisabled?: boolean };

/**
 * Background for one option row, matching the master select: keyboard focus (or
 * hover) outranks the chosen value, selection keeps a lighter tint, disabled stays
 * flat.
 *
 * @param {OptionState} [state] - react-select's per-option state.
 * @returns {string} The background colour for the row.
 */
const optionBackground = (state?: OptionState): string => {
  if (state?.isDisabled) return 'transparent';
  if (state?.isFocused) return COLOR_OPTION_HOVER;
  if (state?.isSelected) return COLOR_OPTION_SELECTED;
  return 'transparent';
};

/** Menu container: rounded, soft-shadowed, borderless card (master-design spec). */
export const statusSelectMenu = (base: StyleBase): StyleBase => ({
  ...base,
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
  marginTop: '4px',
  overflow: 'hidden',
  border: 'none',
  minWidth: '160px',
  zIndex: 6,
});

/** Menu list: 6px padding + 4px gap so each option is an inset rounded row. */
export const statusSelectMenuList = (base: StyleBase): StyleBase => ({
  ...base,
  padding: '6px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

/**
 * Option row: master-design typography and inset rounded focus/selected fill —
 * 12px medium text, 4px radius, light-blue hover (#ceeafc), lighter selected tint.
 *
 * @param {StyleBase} base - react-select's computed base styles.
 * @param {OptionState} [state] - react-select's per-option state.
 * @returns {StyleBase} The merged option style.
 */
export const statusSelectOption = (base: StyleBase, state?: OptionState): StyleBase => ({
  ...base,
  minHeight: '24px',
  height: 'auto',
  flexShrink: 0,
  padding: '4px 6px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '4px',
  lineHeight: '16px',
  whiteSpace: 'normal',
  fontFamily: FONT_MEDIUM,
  fontSize: '12px',
  fontWeight: 500,
  color: state?.isDisabled ? '#a4a4a4' : COLOR_TEXT,
  cursor: state?.isDisabled ? 'not-allowed' : 'pointer',
  opacity: state?.isDisabled ? 0.5 : 1,
  backgroundColor: optionBackground(state),
  ':hover': { backgroundColor: state?.isDisabled ? 'transparent' : COLOR_OPTION_HOVER },
  ':active': { backgroundColor: COLOR_OPTION_HOVER },
});

/** All three menu-related overrides, ready to spread into a `styles` object. */
export const statusSelectMenuStyles = {
  menu: statusSelectMenu,
  menuList: statusSelectMenuList,
  option: statusSelectOption,
};
